<template>
  <div class="ai-chat-widget">
    <transition name="ai-chat-slide">
      <div v-if="open" class="ai-chat-panel">
        <div class="ai-chat-header">
          <div>
            <div class="ai-chat-title">智能问答</div>
            <div class="ai-chat-subtitle">中医古籍网站智能助手</div>
          </div>
          <div class="ai-chat-actions">
            <button type="button" class="text-action" @click="toggleSessionList">
              {{ showSessionList ? '对话' : '历史' }}
            </button>
            <button type="button" class="close-action" @click="open = false">×</button>
          </div>
        </div>

        <div v-if="showSessionList" class="session-panel">
          <div class="session-toolbar">
            <button type="button" class="primary-action" @click="startNewSession">新会话</button>
            <button type="button" class="plain-action" @click="loadSessions">刷新</button>
          </div>
          <div v-if="sessionLoading" class="empty-state">正在加载会话...</div>
          <div v-else-if="sessions.length === 0" class="empty-state">暂无历史会话</div>
          <div v-else class="session-list">
            <div
              v-for="item in sessions"
              :key="item.id"
              class="session-item"
              :class="{ active: item.id === sessionId }"
              @click="selectSession(item)"
            >
              <div class="session-info">
                <div class="session-title">{{ item.title || '新会话' }}</div>
                <div class="session-meta">
                  {{ item.lastMessageTime || item.updateTime || item.createTime }}
                </div>
              </div>
              <div class="session-ops">
                <button type="button" title="重命名" @click.stop="renameSession(item)">改名</button>
                <button type="button" title="删除" @click.stop="deleteSession(item)">删除</button>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="message-panel" ref="messagePanel">
          <div v-if="messages.length === 0" class="welcome-card">
            <div class="welcome-title">可以向我提问</div>
            <div class="welcome-text">
              我可以围绕中医古籍、文献阅读、知识理解等问题提供参考回答。
            </div>
          </div>
          <div v-if="messages.length === 0" class="preset-card">
            <div class="preset-head">
              <span class="preset-title">推荐问题</span>
              <button
                type="button"
                class="preset-refresh"
                :disabled="presetLoading"
                @click="loadPresetQuestions(true)"
              >
                {{ presetLoading ? '加载中' : '换一批' }}
              </button>
            </div>
            <div v-if="presetLoading && presetQuestions.length === 0" class="preset-empty">
              正在取出常用问题...
            </div>
            <div v-else-if="presetQuestions.length === 0" class="preset-empty">
              暂无推荐问题
            </div>
            <div v-else class="preset-list">
              <button
                v-for="item in presetQuestions"
                :key="item.id || item.content"
                type="button"
                class="preset-chip"
                :disabled="sending"
                @click="askPresetQuestion(item.content)"
              >
                {{ item.content }}
              </button>
            </div>
          </div>
          <div
            v-for="message in messages"
            :key="message.localId || message.id"
            class="message-row"
            :class="message.role === 'user' ? 'is-user' : 'is-assistant'"
          >
            <div class="message-bubble">
              <div class="message-role">{{ message.role === 'user' ? '我' : '智能问答' }}</div>
              <!-- 写在一行：message-content 是 pre-wrap，元素间换行会被当成可见空白 -->
              <div class="message-content"><span v-if="!message.content && message.streaming">正在思考...</span><template v-else>{{ message.content }}</template><span v-if="message.streaming && message.content" class="stream-cursor">▍</span></div>
            </div>
          </div>
        </div>

        <div v-if="!showSessionList" class="composer">
          <textarea
            v-model="question"
            class="composer-input"
            maxlength="4000"
            placeholder="请输入问题，Enter 发送，Shift+Enter 换行"
            @keydown.enter.exact.prevent="sendMessage"
          />
          <div class="composer-footer">
            <span>{{ question.length }}/4000</span>
            <button type="button" class="primary-action send-action" :disabled="sending || !question.trim()" @click="sendMessage">
              发送
            </button>
          </div>
        </div>
      </div>
    </transition>

    <button type="button" class="float-button" :class="{ active: open }" @click="handleToggle">
      <span class="float-symbol">问</span>
      <span class="float-label">智能问答</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'AiChatWidget',
  data () {
    return {
      open: false,
      showSessionList: false,
      question: '',
      sessionId: '',
      messages: [],
      sessions: [],
      sending: false,
      sessionLoading: false,
      presetQuestions: [],
      presetLoading: false,
      presetCategory: {
        categoryType: 'common',
        categoryValue: 'common'
      },
      presetBookId: ''
    }
  },
  created () {
    this.presetCategory = this.getStoredPresetCategory()
    this.resetCategoryIfOutsideHome(this.$route && this.$route.path)
    this.presetBookId = this.getRouteBookId()
    if (this.$bus) {
      this.$bus.$on('ai-chat-category-change', this.handlePresetCategoryChange)
      this.$bus.$on('ai-chat-ask', this.handleExternalAsk)
    }
  },
  watch: {
    '$route.path': function (path) {
      this.resetCategoryIfOutsideHome(path)
      this.handleRouteBookIdChange()
    },
    '$route.query.book': function () {
      this.handleRouteBookIdChange()
    }
  },
  beforeDestroy () {
    if (this.$bus) {
      this.$bus.$off('ai-chat-category-change', this.handlePresetCategoryChange)
      this.$bus.$off('ai-chat-ask', this.handleExternalAsk)
    }
  },
  methods: {
    isSuccess (res) {
      return res && res.status === 200 && res.data && res.data.status === 200
    },
    getErrorMessage (error, fallback) {
      if (error && error.data && error.data.message) {
        return error.data.message
      }
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message
      }
      if (error && error.message) {
        return error.message
      }
      return fallback || '操作失败，请稍后重试'
    },
    handleToggle () {
      this.open = !this.open
      if (this.open && this.sessions.length === 0) {
        this.loadSessions()
      }
      if (this.open && this.presetQuestions.length === 0) {
        this.loadPresetQuestions()
      }
      this.$nextTick(this.scrollToBottom)
    },
    toggleSessionList () {
      this.showSessionList = !this.showSessionList
      if (this.showSessionList) {
        this.loadSessions()
      } else {
        this.$nextTick(this.scrollToBottom)
      }
    },
    getVisitorId () {
      var key = 'aiChatVisitorId'
      var visitorId = localStorage.getItem(key)
      if (!visitorId) {
        visitorId = 'guest-' + Date.now() + '-' + Math.random().toString(16).slice(2)
        localStorage.setItem(key, visitorId)
      }
      return visitorId
    },
    getIdentity () {
      var isUser = localStorage.getItem('isLogined') === 'true' && localStorage.getItem('userName') !== 'anonymity'
      if (isUser && localStorage.getItem('userId')) {
        return {
          userId: localStorage.getItem('userId')
        }
      }
      return {
        visitorId: this.getVisitorId()
      }
    },
    appendIdentityToParams (params) {
      var identity = this.getIdentity()
      if (identity.userId) {
        params.append('userId', identity.userId)
      } else {
        params.append('visitorId', identity.visitorId)
      }
      return params
    },
    getSourceUrl () {
      return window.location.pathname + window.location.search
    },
    getCommonPresetCategory () {
      return {
        categoryType: 'common',
        categoryValue: 'common',
        categoryName: '通用问题'
      }
    },
    storePresetCategory (category) {
      if (!category || !category.categoryType || !category.categoryValue) return
      this.$global.aiChatCategory = category
      localStorage.setItem('aiChatCategory', JSON.stringify(category))
    },
    getStoredPresetCategory () {
      var fallback = this.getCommonPresetCategory()
      var stored = localStorage.getItem('aiChatCategory')
      if (!stored) return fallback
      try {
        var parsed = JSON.parse(stored)
        if (parsed && parsed.categoryType && parsed.categoryValue) {
          return {
            categoryType: parsed.categoryType,
            categoryValue: String(parsed.categoryValue)
          }
        }
      } catch (e) {
        return fallback
      }
      return fallback
    },
    resetCategoryIfOutsideHome (path) {
      if (!path || path === '/home') return
      var category = this.getCommonPresetCategory()
      this.storePresetCategory(category)
      this.handlePresetCategoryChange(category)
    },
    handlePresetCategoryChange (category) {
      if (!category || !category.categoryType || !category.categoryValue) return
      var next = {
        categoryType: category.categoryType,
        categoryValue: String(category.categoryValue)
      }
      if (next.categoryType === this.presetCategory.categoryType &&
        next.categoryValue === this.presetCategory.categoryValue) {
        return
      }
      this.presetCategory = next
    },
    getRouteBookId () {
      if (!this.$route) return ''
      var path = this.$route.path || ''
      if (path.indexOf('/bookDetail') === -1) return ''
      var book = this.$route.query && this.$route.query.book
      return book ? String(book) : ''
    },
    handleRouteBookIdChange () {
      var next = this.getRouteBookId()
      if (next === this.presetBookId) return
      this.presetBookId = next
      this.presetQuestions = []
      if (this.open) {
        this.loadPresetQuestions(true)
      }
    },
    startNewSession () {
      this.sessionId = ''
      this.messages = []
      this.question = ''
      this.showSessionList = false
      this.loadPresetQuestions(true)
    },
    selectSession (session) {
      this.sessionId = session.id
      this.showSessionList = false
      this.loadMessages(session.id)
    },
    loadSessions () {
      var params = this.appendIdentityToParams(new URLSearchParams())
      params.append('pageNum', 1)
      params.append('pageSize', 20)
      this.sessionLoading = true
      this.$http.get('/retrieve/aiChat/getSessionList?' + params.toString(), { timeout: 30000 })
        .then(res => {
          if (this.isSuccess(res)) {
            this.sessions = (res.data.data && res.data.data.sessionList) || []
          }
        })
        .catch(error => {
          this.$message.error(this.getErrorMessage(error, '会话加载失败'))
        })
        .finally(() => {
          this.sessionLoading = false
        })
    },
    loadPresetQuestions (force) {
      if (this.presetLoading) return
      if (!force && this.presetQuestions.length > 0) return
      this.presetLoading = true
      var params = new URLSearchParams()
      params.append('limit', 3)
      var bookId = this.getRouteBookId()
      if (bookId) {
        params.append('bookId', bookId)
      }
      this.$http.get('/retrieve/aiChat/getPresetQuestions?' + params.toString(), { timeout: 30000 })
        .then(res => {
          if (this.isSuccess(res)) {
            var data = res.data.data || {}
            if (data._debug) {
              // eslint-disable-next-line no-console
              console.log('[preset-resolve] ' + JSON.stringify(data._debug))
            }
            var list = data.list || []
            this.presetQuestions = list.filter(function (item) {
              return item && item.content
            }).slice(0, 3)
          }
        })
        .catch(error => {
          this.$message.error(this.getErrorMessage(error, '推荐问题加载失败'))
        })
        .finally(() => {
          this.presetLoading = false
        })
    },
    askPresetQuestion (content) {
      if (!content || this.sending) return
      this.question = content
      this.sendMessage()
    },
    handleExternalAsk (content) {
      if (!content) return
      if (this.sending) {
        this.$message.warning('智能问答正在回答，请稍后再试')
        return
      }
      this.open = true
      this.showSessionList = false
      this.$nextTick(() => {
        this.askPresetQuestion(content)
      })
    },
    loadMessages (sessionId) {
      if (!sessionId) return
      var params = this.appendIdentityToParams(new URLSearchParams())
      params.append('sessionId', sessionId)
      params.append('pageNum', 1)
      params.append('pageSize', 50)
      this.$http.get('/retrieve/aiChat/getMessageList?' + params.toString(), { timeout: 30000 })
        .then(res => {
          if (this.isSuccess(res)) {
            var list = (res.data.data && res.data.data.messageList) || []
            this.messages = list.map(function (item) {
              return {
                id: item.id,
                role: item.role,
                content: item.content,
                createTime: item.createTime
              }
            })
            this.$nextTick(this.scrollToBottom)
          }
        })
        .catch(error => {
          this.$message.error(this.getErrorMessage(error, '消息加载失败'))
        })
    },
    renameSession (session) {
      var oldTitle = session.title || '新会话'
      var title = window.prompt('请输入会话标题', oldTitle)
      if (title === null) return
      title = title.trim()
      if (!title) {
        this.$message.warning('标题不能为空')
        return
      }
      var params = this.appendIdentityToParams(new URLSearchParams())
      params.append('sessionId', session.id)
      params.append('title', title)
      this.$http.post('/retrieve/aiChat/updateSessionTitle', params, { timeout: 30000 })
        .then(res => {
          if (this.isSuccess(res)) {
            this.$message.success('会话标题已更新')
            session.title = title
            this.loadSessions()
          }
        })
        .catch(error => {
          this.$message.error(this.getErrorMessage(error, '标题更新失败'))
        })
    },
    deleteSession (session) {
      this.$confirm('删除后该会话将不再显示，是否继续？', '删除会话', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        var params = this.appendIdentityToParams(new URLSearchParams())
        params.append('sessionId', session.id)
        return this.$http.post('/retrieve/aiChat/deleteSession', params, { timeout: 30000 })
      }).then(res => {
        if (this.isSuccess(res)) {
          this.$message.success('会话已删除')
          if (session.id === this.sessionId) {
            this.startNewSession()
          }
          this.loadSessions()
        }
      }).catch(error => {
        if (error !== 'cancel') {
          this.$message.error(this.getErrorMessage(error, '会话删除失败'))
        }
      })
    },
    sendMessage () {
      var text = this.question.trim()
      if (!text || this.sending) return
      var identity = this.getIdentity()
      var body = {
        sessionId: this.sessionId,
        question: text,
        sourceUrl: this.getSourceUrl()
      }
      if (identity.userId) {
        body.userId = Number(identity.userId)
      } else {
        body.visitorId = identity.visitorId
      }

      this.messages.push({
        localId: 'u-' + Date.now(),
        role: 'user',
        content: text
      })
      // 先放一个空的助手气泡，SSE 每收到一个 token 就追加进去，首字即显示
      var assistantMessage = {
        localId: 'a-' + Date.now(),
        id: null,
        role: 'assistant',
        content: '',
        streaming: true
      }
      this.messages.push(assistantMessage)
      this.question = ''
      this.sending = true
      this.$nextTick(this.scrollToBottom)

      // axios 不支持读取流式 body，这里用 fetch；鉴权头与 main.js 的拦截器保持一致
      var headers = { 'Content-Type': 'application/json' }
      if (localStorage.getItem('token')) {
        headers.Authorization = localStorage.getItem('token')
      }
      fetch('/retrieve/aiChat/sendMessageStream', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body)
      }).then(response => {
        if (!response.ok || !response.body) {
          throw new Error('HTTP ' + response.status)
        }
        return this.consumeSseStream(response.body, assistantMessage)
      }).catch(error => {
        var message = this.getErrorMessage(error, '智能问答暂时无法回答，请稍后再试')
        if (assistantMessage.content) {
          this.$message.error(message)
        } else {
          assistantMessage.content = message
        }
      }).finally(() => {
        assistantMessage.streaming = false
        this.sending = false
        this.$nextTick(this.scrollToBottom)
      })
    },
    // 解析后端 SSE 流：event 为 meta / token / complete / error，data 为 JSON
    consumeSseStream (bodyStream, assistantMessage) {
      var reader = bodyStream.getReader()
      var decoder = new TextDecoder('utf-8')
      var buffer = ''
      var finished = false
      var handleEvent = block => {
        var eventName = 'message'
        var dataLines = []
        block.split('\n').forEach(line => {
          if (line.indexOf('event:') === 0) {
            eventName = line.slice(6).trim()
          } else if (line.indexOf('data:') === 0) {
            dataLines.push(line.slice(5).trim())
          }
        })
        if (!dataLines.length) return
        var payload
        try {
          payload = JSON.parse(dataLines.join('\n'))
        } catch (e) {
          return
        }
        if (eventName === 'meta') {
          if (payload.sessionId) this.sessionId = payload.sessionId
        } else if (eventName === 'token') {
          if (payload.token) {
            assistantMessage.content += payload.token
            this.$nextTick(this.scrollToBottom)
          }
        } else if (eventName === 'complete') {
          finished = true
          if (payload.sessionId) this.sessionId = payload.sessionId
          if (payload.messageId) assistantMessage.id = payload.messageId
          // 以后端最终内容为准，避免个别 chunk 丢失
          if (payload.answer) assistantMessage.content = payload.answer
          this.loadSessions()
        } else if (eventName === 'error') {
          finished = true
          var message = payload.message || '智能问答暂时无法回答，请稍后再试'
          if (assistantMessage.content) {
            this.$message.error(message)
          } else {
            assistantMessage.content = message
          }
        }
      }
      var pump = () => reader.read().then(result => {
        if (result.done) {
          if (!finished && !assistantMessage.content) {
            throw new Error('连接已断开，请重试')
          }
          return
        }
        // 统一换行符后按空行切分事件块；跨 chunk 的半个事件留在 buffer 里等下一次
        buffer = (buffer + decoder.decode(result.value, { stream: true })).replace(/\r\n/g, '\n')
        var boundary = buffer.indexOf('\n\n')
        while (boundary !== -1) {
          handleEvent(buffer.slice(0, boundary))
          buffer = buffer.slice(boundary + 2)
          boundary = buffer.indexOf('\n\n')
        }
        return pump()
      })
      return pump()
    },
    scrollToBottom () {
      var panel = this.$refs.messagePanel
      if (panel) {
        panel.scrollTop = panel.scrollHeight
      }
    }
  }
}
</script>

<style scoped>
.ai-chat-widget {
  position: fixed;
  right: 34px;
  bottom: 38px;
  z-index: 3200;
  font-family: myhei, "Avenir", Helvetica, Arial, sans-serif;
  text-align: left;
}

.float-button {
  width: 76px;
  height: 76px;
  border: 0;
  border-radius: 50%;
  background: linear-gradient(145deg, #9e5c1d, #6f421d);
  box-shadow: 0 14px 32px rgba(99, 76, 55, 0.35);
  color: #fff;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.float-button.active {
  background: linear-gradient(145deg, #634c37, #9e5c1d);
}

.float-symbol {
  width: 30px;
  height: 30px;
  line-height: 30px;
  text-align: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  font-family: mysong, serif;
  font-weight: bold;
}

.float-label {
  margin-top: 5px;
  font-size: 12px;
}

.ai-chat-panel {
  position: absolute;
  right: 0;
  bottom: 94px;
  width: 392px;
  height: 570px;
  background: #fffaf5;
  border: 1px solid #e6d4c1;
  box-shadow: 0 18px 45px rgba(99, 76, 55, 0.28);
  display: flex;
  flex-direction: column;
}

.ai-chat-header {
  height: 76px;
  padding: 0 18px;
  background: #634c37;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ai-chat-title {
  font-family: mysong, serif;
  font-size: 22px;
  font-weight: bold;
}

.ai-chat-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #f4e5d8;
}

.ai-chat-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.text-action,
.close-action,
.primary-action,
.plain-action {
  border: 0;
  cursor: pointer;
}

.text-action {
  color: #fff;
  background: rgba(255, 255, 255, 0.15);
  padding: 6px 10px;
}

.close-action {
  width: 26px;
  height: 26px;
  color: #fff;
  font-size: 22px;
  line-height: 22px;
  background: transparent;
}

.message-panel,
.session-panel {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.welcome-card,
.empty-state {
  border: 1px solid #ead9c8;
  background: #fff;
  color: #634c37;
  padding: 16px;
}

.welcome-title {
  font-family: mysong, serif;
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 8px;
}

.preset-card {
  position: relative;
  margin-top: 12px;
  padding: 13px;
  border: 1px solid #ead9c8;
  background:
    linear-gradient(135deg, rgba(255, 250, 245, 0.92), rgba(246, 234, 223, 0.88)),
    repeating-linear-gradient(90deg, rgba(158, 92, 29, 0.05) 0, rgba(158, 92, 29, 0.05) 1px, transparent 1px, transparent 12px);
  box-shadow: inset 3px 0 0 #9e5c1d, 0 4px 13px rgba(99, 76, 55, 0.07);
}

.preset-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.preset-title {
  color: #634c37;
  font-family: mysong, serif;
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 1px;
}

.preset-refresh {
  border: 0;
  background: transparent;
  color: #9e5c1d;
  cursor: pointer;
  font-size: 12px;
}

.preset-refresh:disabled {
  color: #b8a491;
  cursor: not-allowed;
}

.preset-empty {
  color: #9b826d;
  font-size: 13px;
  line-height: 1.7;
}

.preset-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preset-chip {
  width: 100%;
  border: 1px solid #e1cbb6;
  background: rgba(255, 255, 255, 0.8);
  color: #3c2e23;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.55;
  padding: 8px 10px;
  text-align: left;
  transition: all 0.18s ease;
}

.preset-chip:hover:not(:disabled) {
  border-color: #9e5c1d;
  background: #fff;
  color: #9e5c1d;
  transform: translateX(3px);
}

.preset-chip:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.welcome-text,
.empty-state {
  font-size: 14px;
  line-height: 1.7;
}

.message-row {
  display: flex;
  margin: 12px 0;
}

.message-row.is-user {
  justify-content: flex-end;
}

.message-bubble {
  max-width: 82%;
  padding: 10px 12px;
  background: #fff;
  color: #3c2e23;
  border: 1px solid #ecdccd;
  box-shadow: 0 3px 10px rgba(99, 76, 55, 0.08);
}

.is-user .message-bubble {
  background: #9e5c1d;
  color: #fff;
  border-color: #9e5c1d;
}

.message-role {
  font-size: 12px;
  opacity: 0.72;
  margin-bottom: 5px;
}

.message-content {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.65;
}

.stream-cursor {
  margin-left: 2px;
  opacity: 0.65;
}

.composer {
  padding: 12px 14px 14px;
  background: #fff;
  border-top: 1px solid #ead9c8;
}

.composer-input {
  width: 100%;
  height: 82px;
  resize: none;
  box-sizing: border-box;
  border: 1px solid #d9c1ab;
  outline: none;
  padding: 10px;
  color: #3c2e23;
  background: #fffdfb;
  font-family: inherit;
  font-size: 14px;
}

.composer-input:focus {
  border-color: #9e5c1d;
}

.composer-footer,
.session-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  color: #9b826d;
  font-size: 12px;
}

.primary-action {
  background: #9e5c1d;
  color: #fff;
  padding: 8px 14px;
}

.primary-action:disabled {
  background: #cbb7a3;
  cursor: not-allowed;
}

.plain-action {
  background: #f3e7dc;
  color: #634c37;
  padding: 8px 14px;
}

.send-action {
  min-width: 72px;
}

.session-list {
  margin-top: 12px;
}

.session-item {
  padding: 12px;
  margin-bottom: 10px;
  background: #fff;
  border: 1px solid #ead9c8;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.session-item.active {
  border-color: #9e5c1d;
  background: #f6eadf;
}

.session-title {
  color: #3c2e23;
  font-size: 15px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.session-info {
  flex: 1;
  min-width: 0;
}

.session-ops {
  display: flex;
  align-items: center;
  gap: 5px;
}

.session-ops button {
  border: 0;
  background: #f3e7dc;
  color: #634c37;
  font-size: 12px;
  padding: 5px 7px;
  cursor: pointer;
}

.session-ops button:hover {
  background: #9e5c1d;
  color: #fff;
}

.session-meta {
  margin-top: 6px;
  color: #9b826d;
  font-size: 12px;
}

.ai-chat-slide-enter-active,
.ai-chat-slide-leave-active {
  transition: all 0.2s ease;
}

.ai-chat-slide-enter,
.ai-chat-slide-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (max-width: 768px) {
  .ai-chat-widget {
    right: 16px;
    bottom: 18px;
  }

  .ai-chat-panel {
    width: 90vw;
    height: 70vh;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/AiChatWidget.vue